import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useIntl } from 'react-intl'

import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'
import { ChooserDialog, type ChooserItem } from '@/shared/search'
import { TextField } from '@/shared/ui'

import { parseUserGroupSummary, userGroupsApi, type UserGroupSummary } from './user-group'

/** A user group a chooser hands back: its public id and name. */
export interface ChosenUserGroup {
  readonly id: string
  readonly name: string
}

interface UserGroupChooserBase {
  readonly open: boolean
  readonly onClose: () => void
  /** Offers only the groups this person may join, by their User Profile's public id. */
  readonly availableTo?: string
  /** Left out of the results, such as the groups a person is already in. */
  readonly excludeIds?: readonly string[]
  /** The dialog's title; "Select User Group" by default. */
  readonly title?: string
}

export type UserGroupChooserProps = UserGroupChooserBase &
  (
    | { readonly multi?: false; readonly onChoose: (group: ChosenUserGroup) => void }
    | { readonly multi: true; readonly onChoose: (groups: readonly ChosenUserGroup[]) => void }
  )

interface Results {
  readonly items: readonly ChooserItem[]
  readonly next: string | null
}

function sublabel(group: UserGroupSummary): string {
  return group.companyName === null || group.companyName === undefined ? group.organizationName : `${group.organizationName} · ${group.companyName}`
}

/**
 * "Select User Group", from the mockups' chooser: search the site's user groups by name, pick one,
 * or several with `multi`, then OK hands them back. Each shows its organization, and its company
 * when it has one, under its name.
 */
export function UserGroupChooser(props: UserGroupChooserProps) {
  const intl = useIntl()
  const client = useApiClient()
  const { open, onClose, availableTo } = props
  const excluded = props.excludeIds
  const [name, setName] = useState('')
  const [results, setResults] = useState<Results | null>(null)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [picked, setPicked] = useState<readonly ChooserItem[]>([])
  // The text the shown results were searched with, so Load More continues that search, not the one being typed.
  const [searched, setSearched] = useState('')
  const request = useRef<AbortController | null>(null)

  // Starts clean each time it opens.
  useEffect(() => {
    if (open) {
      // oxlint-disable-next-line react/set-state-in-effect -- resets the dialog as it opens
      setName('')
      setSearched('')
      setResults(null)
      setLoading(false)
      setFailed(false)
      setPicked([])
    }
    return () => request.current?.abort()
  }, [open])

  const load = useCallback(
    async (text: string, cursor: string | null) => {
      request.current?.abort()
      const controller = new AbortController()
      request.current = controller
      setLoading(true)
      setFailed(false)
      // A new search drops the last one's rows at once, so none that no longer match can be picked.
      if (cursor === null) {
        setResults(null)
      }
      const query = new URLSearchParams({ sort: 'name', limit: '25' })
      if (text !== '') {
        query.set('q', text)
      }
      if (availableTo !== undefined) {
        query.set('availableTo', availableTo)
      }
      if (cursor !== null) {
        query.set('cursor', cursor)
      }
      try {
        const page = parseCursorPage(await client.get(`${userGroupsApi}?${query.toString()}`, controller.signal), parseUserGroupSummary)
        const items = page.data
          .filter((group) => excluded?.includes(group.id) !== true)
          .map((group): ChooserItem => ({ id: group.id, label: group.name, sublabel: sublabel(group) }))
        setResults((current) => ({
          items: cursor === null || current === null ? items : [...current.items, ...items],
          next: page.pagination.nextCursor,
        }))
      } catch {
        if (!controller.signal.aborted) {
          setFailed(true)
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    },
    [client, availableTo, excluded],
  )

  const multi = props.multi === true
  const search = useCallback(() => {
    const text = name.trim()
    setSearched(text)
    // A pick from the last search's rows is gone with them.
    setPicked([])
    void load(text, null)
  }, [load, name])
  const loadMore = useCallback(() => void load(searched, results?.next ?? null), [load, searched, results])
  const toggle = useCallback(
    (item: ChooserItem) =>
      setPicked((current) => {
        if (!multi) {
          return [item]
        }
        return current.some((chosen) => chosen.id === item.id) ? current.filter((chosen) => chosen.id !== item.id) : [...current, item]
      }),
    [multi],
  )
  const confirm = useCallback(() => {
    const chosen = picked.map((item) => ({ id: item.id, name: item.label }))
    if (props.multi === true) {
      props.onChoose(chosen)
    } else if (chosen[0] !== undefined) {
      props.onChoose(chosen[0])
    }
    onClose()
  }, [picked, props, onClose])
  const pickedIds = useMemo(() => picked.map((item) => item.id), [picked])
  const changeName = useCallback((event: ChangeEvent<HTMLInputElement>) => setName(event.target.value), [])

  const shared = {
    open,
    onClose,
    title: props.title ?? intl.formatMessage({ id: 'siteManagement.userGroups.chooser.title' }),
    filters: (
      <TextField
        label={intl.formatMessage({ id: 'siteManagement.userGroups.field.name' })}
        placeholder={intl.formatMessage({ id: 'siteManagement.userGroups.chooser.placeholder' })}
        value={name}
        onChange={changeName}
      />
    ),
    onSearch: search,
    results: results?.items ?? null,
    loading,
    failed,
    hasMore: results !== null && results.next !== null,
    onLoadMore: loadMore,
    onToggle: toggle,
    onConfirm: confirm,
  }
  return multi ? (
    <ChooserDialog {...shared} multi selected={pickedIds} />
  ) : (
    <ChooserDialog {...shared} selected={picked[0]?.id ?? null} />
  )
}
